import React, { useState, useEffect, useRef } from 'react';
import { X, Save, Plus, Type, Mic, BookOpen, Quote, Trash2 } from 'lucide-react';
import { collection, addDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, auth, storage } from './firebase';

const AddWordModal = ({ onClose, initialData = null, isAdmin = false, lang, t }) => {
    // ... imports and state ...
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const headerRef = useRef(null);
    const [headerFontSize, setHeaderFontSize] = useState(null);

    // Form State
    const [word, setWord] = useState('');
    const [phonetic, setPhonetic] = useState('');
    const [audio, setAudio] = useState('');
    const [audioFile, setAudioFile] = useState(null);
    const [seeAlso, setSeeAlso] = useState('');
    const [uploading, setUploading] = useState(false);

    // Multiple Meanings State
    const [meanings, setMeanings] = useState([
        { id: Date.now(), partOfSpeech: 'noun', definition: '', example: '' }
    ]);

    const titleText = initialData ? (initialData.isRequest ? (t?.editRequestTitle || 'Edit Request') : (t?.editWordTitle || 'Edit Word')) : (isAdmin ? (t?.addWordTitle || 'Add New Word') : (t?.suggestWordTitle || 'Suggest New Word'));

    useEffect(() => {
        const adjustFontSize = () => {
            const element = headerRef.current;
            if (!element) return;

            let currentSize = 30; // Start with base size (approx text-3xl)
            const minSize = 14;

            // Apply base size to measure
            element.style.fontSize = `${currentSize}px`;
            element.style.whiteSpace = 'nowrap';

            // Reduce if overflowing
            while (element.scrollWidth > element.clientWidth && currentSize > minSize) {
                currentSize--;
                element.style.fontSize = `${currentSize}px`;
            }

            setHeaderFontSize(currentSize);
        };

        adjustFontSize();
        window.addEventListener('resize', adjustFontSize);
        return () => window.removeEventListener('resize', adjustFontSize);
    }, [titleText]);

    // ... useEffect ...
    useEffect(() => {
        if (initialData) {
            setWord(initialData.word || '');
            if (initialData.phonetics && initialData.phonetics.length > 0) {
                setPhonetic(initialData.phonetics[0].text || '');
                setAudio(initialData.phonetics[0].audio || '');
            }
            setSeeAlso(initialData.seeAlso || '');

            if (initialData.meanings && initialData.meanings.length > 0) {
                const loadedMeanings = initialData.meanings.map((m, idx) => ({
                    id: Date.now() + idx,
                    partOfSpeech: m.partOfSpeech || 'noun',
                    definition: m.definitions?.[0]?.definition || '',
                    example: m.definitions?.[0]?.example || ''
                }));
                setMeanings(loadedMeanings);
            }
        }
    }, [initialData]);

    const addMeaning = () => {
        setMeanings([...meanings, { id: Date.now(), partOfSpeech: 'noun', definition: '', example: '' }]);
    };

    const removeMeaning = (id) => {
        if (meanings.length > 1) {
            setMeanings(meanings.filter(m => m.id !== id));
        }
    };

    const updateMeaning = (id, field, value) => {
        setMeanings(meanings.map(m => m.id === id ? { ...m, [field]: value } : m));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        if (!auth.currentUser) {
            setError("You must be logged in.");
            setLoading(false);
            return;
        }

        try {
            let audioUrl = audio;

            if (audioFile) {
                setUploading(true);
                const storageRef = ref(storage, `audio/${Date.now()}_${audioFile.name}`);
                const snapshot = await uploadBytes(storageRef, audioFile);
                audioUrl = await getDownloadURL(snapshot.ref);
                setUploading(false);
            }

            const docData = {
                word: word.toLowerCase(),
                seeAlso: seeAlso,
                authorEmail: initialData?.authorEmail || auth.currentUser.email,
                updatedAt: serverTimestamp(),
                phonetics: [
                    {
                        text: phonetic,
                        audio: audioUrl
                    }
                ],
                meanings: meanings.map(m => ({
                    partOfSpeech: m.partOfSpeech,
                    definitions: [
                        {
                            definition: m.definition,
                            example: m.example
                        }
                    ]
                }))
            };

            if (!initialData) {
                docData.createdAt = serverTimestamp();
                const collectionName = isAdmin ? "words" : "word_requests";
                await addDoc(collection(db, collectionName), docData);
                alert(isAdmin ? "Word added successfully!" : "Request submitted! An admin will review it.");
            } else {
                const collectionToUpdate = initialData.isRequest ? "word_requests" : "words";
                const wordRef = doc(db, collectionToUpdate, initialData.id);
                await updateDoc(wordRef, docData);
                alert(initialData.isRequest ? "Request updated successfully!" : "Word updated successfully!");
            }

            onClose();
        } catch (err) {
            console.error("Error saving word: ", err);
            alert("Failed to save word. Check your permissions.");
        } finally {
            setLoading(false);
            setUploading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            ></div>

            {/* Modal Card */}
            <div className="relative bg-white border-4 border-black p-6 md:p-8 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] w-full max-w-2xl max-h-[90vh] overflow-y-auto">

                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 bg-red-500 border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:translate-y-1 hover:shadow-none transition-all"
                >
                    <X size={20} className="text-white" strokeWidth={3} />
                </button>

                {/* Header */}
                <h2
                    ref={headerRef}
                    className="text-3xl font-black uppercase mb-6 flex items-center gap-2 mr-12"
                    style={{
                        fontSize: headerFontSize ? `${headerFontSize}px` : undefined,
                        whiteSpace: 'nowrap'
                    }}
                >
                    <Plus size={32} strokeWidth={4} className="text-[#7ED957]" />
                    <Plus size={32} strokeWidth={4} className="text-[#7ED957]" />
                    {titleText}
                </h2>

                {/* Form */}
                <form onSubmit={handleSubmit} className="flex flex-col gap-6">

                    {/* Section: The Word */}
                    <div className="bg-[#5CE1E6] p-4 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                        <label className="font-black uppercase mb-2 block">{t?.wordIdSection || 'Word Identification'}</label>
                        <div className="flex flex-col gap-4">
                            {/* Word Input */}
                            <div className="relative">
                                <Type className="absolute top-3 left-3 text-gray-500" size={20} />
                                <input
                                    required
                                    value={word}
                                    onChange={e => setWord(e.target.value)}
                                    className="w-full p-3 pl-10 border-2 border-black font-bold focus:outline-none focus:ring-2 ring-black"
                                    placeholder={t?.wordLabel ? `e.g. ${t.wordLabel}` : "e.g. Computer"}
                                />
                            </div>

                            {/* Phonetic & Audio */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="relative">
                                    <span className="absolute top-3 left-3 font-serif italic font-black text-lg">/</span>
                                    <input
                                        value={phonetic}
                                        onChange={e => setPhonetic(e.target.value)}
                                        className="w-full p-3 pl-8 border-2 border-black font-bold focus:outline-none focus:ring-2 ring-black"
                                        placeholder={t?.phoneticPlaceholder || "kəmˈpjuːtə"}
                                    />
                                    <span className="absolute top-3 right-3 font-serif italic font-black text-lg">/</span>
                                </div>
                                <div className="relative">
                                    <Mic className="absolute top-3 left-3 text-gray-500" size={20} />
                                    <input
                                        type="file"
                                        accept="audio/*"
                                        onChange={e => setAudioFile(e.target.files[0])}
                                        className="w-full p-3 pl-10 border-2 border-black font-bold focus:outline-none focus:ring-2 ring-black file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-violet-50 file:text-violet-700 hover:file:bg-violet-100"
                                    />
                                    {/* Keep existing audio URL as fallback/display if needed, or remove completely if switching to file only. 
                                        For now replacing input. If editing and existing audio exists, we might want to show it. */}
                                    {initialData?.phonetics?.[0]?.audio && !audioFile && (
                                        <div className="mt-1 text-xs text-green-600 font-bold truncate pl-2">
                                            Current: {initialData.phonetics[0].audio.split('/').pop().split('?')[0].substring(0, 20)}...
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        {/* See Also Input */}
                        <div className="mt-4">
                            <label className="font-bold text-sm uppercase mb-1 block">
                                {t?.seeAlsoLabel || "See Also"}
                            </label>
                            <input
                                value={seeAlso}
                                onChange={e => setSeeAlso(e.target.value)}
                                className="w-full p-3 border-2 border-black font-bold focus:outline-none focus:ring-2 ring-black"
                                placeholder={t?.seeAlsoLabel || "Related word..."}
                            />
                        </div>
                    </div>

                    {/* Section: Meanings */}
                    <div className="flex flex-col gap-4">
                        {meanings.map((meaning, index) => (
                            <div key={meaning.id} className="bg-[#FFDE59] p-4 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] relative">
                                <div className="flex justify-between items-center mb-2">
                                    <label className="font-black uppercase block">{t?.meaningLabel || 'Meaning'} #{index + 1}</label>
                                    {meanings.length > 1 && (
                                        <button type="button" onClick={() => removeMeaning(meaning.id)} className="text-red-600 hover:text-red-800">
                                            <Trash2 size={20} />
                                        </button>
                                    )}
                                </div>

                                <div className="flex flex-col gap-4">
                                    {/* Part of Speech */}
                                    <select
                                        value={meaning.partOfSpeech}
                                        onChange={e => updateMeaning(meaning.id, 'partOfSpeech', e.target.value)}
                                        className="w-full p-3 border-2 border-black font-bold focus:outline-none focus:ring-2 ring-black bg-white"
                                    >
                                        <option value="noun">{t?.partOfSpeech?.noun || 'Noun'}</option>
                                        <option value="verb">{t?.partOfSpeech?.verb || 'Verb'}</option>
                                        <option value="adjective">{t?.partOfSpeech?.adjective || 'Adjective'}</option>
                                        <option value="adverb">{t?.partOfSpeech?.adverb || 'Adverb'}</option>
                                        <option value="pronoun">{t?.partOfSpeech?.pronoun || 'Pronoun'}</option>
                                        <option value="preposition">{t?.partOfSpeech?.preposition || 'Preposition'}</option>
                                        <option value="conjunction">{t?.partOfSpeech?.conjunction || 'Conjunction'}</option>
                                        <option value="interjection">{t?.partOfSpeech?.interjection || 'Interjection'}</option>
                                    </select>

                                    {/* Definition */}
                                    <div className="relative">
                                        <BookOpen className="absolute top-3 left-3 text-gray-500" size={20} />
                                        <textarea
                                            required
                                            value={meaning.definition}
                                            onChange={e => updateMeaning(meaning.id, 'definition', e.target.value)}
                                            rows={2}
                                            className="w-full p-3 pl-10 border-2 border-black font-bold focus:outline-none focus:ring-2 ring-black resize-none"
                                            placeholder={t?.defPlaceholder || "Enter the definition..."}
                                        />
                                    </div>

                                    {/* Example */}
                                    <div className="relative">
                                        <Quote className="absolute top-3 left-3 text-gray-500" size={20} />
                                        <input
                                            value={meaning.example}
                                            onChange={e => updateMeaning(meaning.id, 'example', e.target.value)}
                                            className="w-full p-3 pl-10 border-2 border-black font-mono italic text-sm focus:outline-none focus:ring-2 ring-black"
                                            placeholder={t?.exPlaceholder || "e.g. Usage example sentence..."}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Add Meaning Button */}
                    <button
                        type="button"
                        onClick={addMeaning}
                        className="py-2 border-2 border-dashed border-gray-400 font-bold text-gray-500 hover:border-black hover:text-black hover:bg-gray-50 transition-colors"
                    >
                        {t?.addMeaningBtn || '+ Add Another Meaning'}
                    </button>

                    {/* Error */}
                    {error && (
                        <div className="p-3 bg-red-100 border-2 border-black font-bold text-red-600 text-center">
                            {error}
                        </div>
                    )}

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={loading || uploading}
                        className="h-14 bg-[#8c52ff] text-white text-xl font-black border-4 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:translate-x-[4px] active:translate-y-[4px] active:shadow-none transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                        {uploading ? (t?.uploading || 'UPLOADING...') : (loading ? (t?.saving || 'SAVING...') : (initialData ? (t?.updateBtn || 'UPDATE WORD') : (isAdmin ? (t?.saveBtn || 'SAVE WORD') : (t?.submitBtn || 'SUBMIT REQUEST'))))}
                        {!loading && !uploading && <Save size={24} strokeWidth={3} />}
                    </button>
                </form>

            </div>
        </div>
    );
};

export default AddWordModal;
